/**
 * annotationDecorationPlugin — paints author-only annotation tokens
 * ([TODO: …], [ATT: …]) as inline chips WITHOUT mutating the document
 * (spec 19). Same mechanism as citationDecorationPlugin: the token text
 * remains real, visible, editable content; the decoration only styles it.
 * Export strips these tokens server-side unconditionally.
 *
 * Matching is per ProseMirror TEXT NODE, mirroring citationDecorationPlugin.
 * A token broken across marks (bold inside a TODO note) spans several text
 * nodes and is therefore not chipped — the raw token still shows and export
 * still strips it. Pinned by test; changing it means changing both plugins.
 *
 * The same scan also produces the per-section gap count widget rendered at the
 * end of each section heading (spec 19, task 9) — one pass feeds both, so a
 * chip and its count can never disagree. Editor-only by product decision: the
 * navigators carry no pill; the export confirm dialog is the cross-section
 * roll-up.
 */

import { Plugin, PluginKey } from '@tiptap/pm/state'
import { Decoration, DecorationSet } from '@tiptap/pm/view'
import { TODO_REGEX, ATT_REGEX } from '@/lib/annotations'
import { evaluatorStripSpaceDecoration } from '@/components/drafting/citationDecorationPlugin'

export const annotationDecorationPluginKey = new PluginKey('annotationDecoration')

const PATTERNS: Array<{
  regex: RegExp
  annotationType: 'todo' | 'attachment'
  className: string
}> = [
  // TODO first: its payload is bracket-aware, so an [ATT: …] can sit wholly
  // inside a [TODO: …] range. Decorating the outer token first lets the
  // containment check below skip the inner one instead of double-painting it.
  { regex: TODO_REGEX, annotationType: 'todo', className: 'annotation-chip-todo' },
  { regex: ATT_REGEX, annotationType: 'attachment', className: 'annotation-chip-att' },
]

export function createAnnotationDecorationPlugin(): Plugin {
  return new Plugin({
    key: annotationDecorationPluginKey,
    props: {
      decorations(state) {
        const decorations: Decoration[] = []
        const decoratedRanges: Array<{ from: number; to: number }> = []

        function isAlreadyDecorated(from: number, to: number): boolean {
          return decoratedRanges.some((r) => from < r.to && to > r.from)
        }

        // First pass: segment boundaries, in the shape citationDecorationPlugin
        // uses (L42-51) — same document, same attribution rule.
        //
        // Sections are keyed by INDEX here, not by sectionId: a duplicated or
        // empty sectionId (a bad paste, a half-written segment) would otherwise
        // merge two sections' counts into one wrong number.
        const sections: Array<{ pos: number; widgetPos: number | null }> = []
        state.doc.descendants((node, pos, parent, index) => {
          if (node.type.name !== 'segmentHeading') return
          // The boundary atom itself is display:none. The count belongs on the
          // visible editable h2 that assembleEditorDocument emits straight after
          // it (segmentAssembly.ts:178-211). If the author deleted that heading,
          // widgetPos stays null and the section simply gets no count — the
          // chips inside it still render.
          const next = parent?.maybeChild(index + 1) ?? null
          const isVisibleHeading = next != null && next.type.name === 'heading'
          sections.push({
            pos,
            // End of the heading's CONTENT: the heading node itself starts at
            // pos + node.nodeSize, so its content ends one before its close tag.
            widgetPos: isVisibleHeading ? pos + node.nodeSize + next.nodeSize - 1 : null,
          })
        })

        // Which section a position falls in. Boundaries are collected in
        // document order, so the last boundary at or before `pos` wins.
        function sectionIndexAtPos(pos: number): number {
          let found = -1
          for (let i = 0; i < sections.length; i++) {
            if (sections[i]!.pos <= pos) found = i
            else break
          }
          return found
        }

        const todoCounts = new Map<number, number>()

        state.doc.descendants((node, pos) => {
          if (!node.isText || !node.text) return
          const text = node.text

          for (const { regex, annotationType, className } of PATTERNS) {
            // TODO_REGEX/ATT_REGEX are exported /g regexes with shared state, and
            // matchAll COPIES lastIndex into its clone — without this reset a
            // caller's stale lastIndex would silently skip the start of the node.
            regex.lastIndex = 0
            for (const match of text.matchAll(regex)) {
              const from = pos + (match.index ?? 0)
              const to = from + match[0].length
              if (isAlreadyDecorated(from, to)) continue
              decoratedRanges.push({ from, to })
              decorations.push(
                Decoration.inline(from, to, {
                  class: `annotation-chip ${className}`,
                  'data-annotation-type': annotationType,
                  nodeName: 'span',
                })
              )
              // Evaluator view hides every chip; this separate decoration lets it
              // hide the space before the chip too ("gap [TODO: x]." → "gap.").
              const strip = evaluatorStripSpaceDecoration(state.doc, from, to)
              if (strip) decorations.push(strip)

              // Count what the author can actually SEE chipped, which is why the
              // tally sits after the overlap guard rather than beside the match.
              // In practice the guard never skips a TODO — PATTERNS runs TODO
              // first and its payload is bracket-aware, so the only containment
              // that can occur is an ATT inside a TODO — but tying the count to
              // the emitted decoration keeps chip and count in lockstep if that
              // ordering is ever changed. ATT tokens are attribution, not gaps,
              // and never count.
              if (annotationType === 'todo') {
                const sectionIndex = sectionIndexAtPos(from)
                if (sectionIndex >= 0) {
                  todoCounts.set(sectionIndex, (todoCounts.get(sectionIndex) ?? 0) + 1)
                }
              }
            }
          }
        })

        // Third pass: one gap-count widget per section that has open TODOs.
        // The widget is pure decoration — it adds no text to the document, so
        // word counts, exports and diffs never see it.
        for (const [sectionIndex, section] of sections.entries()) {
          const count = todoCounts.get(sectionIndex) ?? 0
          if (count === 0 || section.widgetPos === null) continue

          const label = count === 1 ? '1 gap' : `${count} gaps`
          decorations.push(
            Decoration.widget(
              section.widgetPos,
              () => {
                const el = document.createElement('span')
                el.className = 'annotation-gap-count'
                el.setAttribute('contenteditable', 'false')
                el.textContent = label
                return el
              },
              // `key` lets ProseMirror reuse the node across redraws; it carries
              // the count so a changed tally actually re-renders. `side: 1`
              // parks the widget after the heading text, and `ignoreSelection`
              // stops it swallowing a click meant for the heading.
              { key: `annotation-gap-count-${sectionIndex}-${count}`, side: 1, ignoreSelection: true }
            )
          )
        }

        return DecorationSet.create(state.doc, decorations)
      },
    },
  })
}
